import { create } from 'zustand';
import { authService, type User, type AuthState } from '../lib/auth';

interface AuthStore extends AuthState {
  login: (token: string, user: User) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  startImpersonation: (impersonatedUser: User) => void;
  stopImpersonation: () => void;
  isImpersonating: boolean;
}

export const useAuth = create<AuthStore>((set) => {
  // Set up the logout callback so auth service can trigger store updates
  const logoutCallback = () => {
    set({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };
  
  authService.setLogoutCallback(logoutCallback);

  return {
    user: authService.getUser(),
    token: authService.getToken(),
    isAuthenticated: authService.isAuthenticated(),
    isLoading: false,
    isImpersonating: authService.isImpersonating(),
    login: (token: string, user: User) => {
      authService.setAuth(token, user);
      set({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
        isImpersonating: false,
      });
    },
    logout: () => {
      authService.logout();
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        isImpersonating: false,
      });
    },
    startImpersonation: (impersonatedUser: User) => {
      authService.startImpersonation(impersonatedUser);
      set({
        user: impersonatedUser,
        isImpersonating: true,
      });
    },
    stopImpersonation: () => {
      authService.stopImpersonation();
      const restoredUser = authService.getUser();
      set({
        user: restoredUser,
        isImpersonating: false,
      });
    },
    setLoading: (isLoading: boolean) => set({ isLoading }),
  };
});